import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, TrendingUp, Users, Bell, CalendarDays, MessageSquare, Handshake, GraduationCap, Megaphone, Cpu, LifeBuoy } from 'lucide-react';
import { fadeUp, staggerParent } from './motionPresets';

const CONSULT_IMG =
  'https://images.unsplash.com/photo-1780733065500-4284d227f58c?crop=entropy&cs=srgb&fm=jpg&w=900&q=80';

// Growth-chart backdrop echoing the KR logo motif.
const ChartBackdrop = () => (
  <div className="absolute inset-0 opacity-[0.12] pointer-events-none" aria-hidden="true">
    <svg viewBox="0 0 800 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="hero-bar" x1="0" x2="0" y1="1" y2="0">
          <stop offset="0" stopColor="#3b82f6" stopOpacity="0" />
          <stop offset="1" stopColor="#60a5fa" stopOpacity="1" />
        </linearGradient>
      </defs>
      {[80, 160, 240, 320, 400, 490, 580, 670].map((x, i) => (
        <rect key={i} x={x} y={400 - (50 + i * 34)} width="34" height={50 + i * 34} rx="4" fill="url(#hero-bar)" />
      ))}
      <path d="M70,330 C200,290 300,200 430,160 S650,70 780,30" stroke="#b8862d" strokeWidth="4" fill="none" opacity="0.55" />
      <circle cx="780" cy="30" r="7" fill="#f5c876" />
    </svg>
  </div>
);

const scrollToHub = (e) => {
  e.preventDefault();
  document.querySelector('#agency-hub')?.scrollIntoView({ behavior: 'smooth' });
};

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0a1f47] text-white" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-[#071533] via-[#0f2a5e] to-[#1a3a7a]" />
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-[#3b82f6]/20 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-48 -left-32 w-[32rem] h-[32rem] rounded-full bg-[#b8862d]/10 blur-[120px] pointer-events-none" />
      <ChartBackdrop />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-20 sm:pb-28 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Copy */}
        <motion.div
          className="lg:col-span-6"
          variants={staggerParent}
          initial="hidden"
          animate="show"
        >
          <motion.span
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest bg-white/5 text-[#f5c876] border border-[#b8862d]/40 backdrop-blur"
          >
            <TrendingUp size={13} /> Independent agents. Greater opportunities.
          </motion.span>

          <motion.h1
            variants={fadeUp}
            className="mt-6 text-4xl sm:text-5xl lg:text-[3.75rem] font-extrabold tracking-tight leading-[1.05]"
          >
            Build your insurance business with the{' '}
            <span className="text-[#f5c876]">right support.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-xl"
          >
            KR Enterprise Group backs you with carrier opportunities, contracting support, training,
            marketing, and proprietary technology — the full infrastructure behind a modern agency.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-9 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
            <Link
              to="/become-an-agent"
              data-testid="hero-cta-become"
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#b8862d] text-white font-semibold shadow-[0_10px_30px_-8px_rgba(184,134,45,0.6)] hover:bg-[#a67520] hover:-translate-y-0.5 transition-all duration-300"
            >
              Become an Agent
              <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <a
              href="#agency-hub"
              onClick={scrollToHub}
              data-testid="hero-cta-explore-hub"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white/5 border border-white/20 text-white font-semibold hover:bg-white/10 hover:border-white/40 transition-all duration-300 backdrop-blur"
            >
              Explore KR Agency Hub
            </a>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400">
            <span className="inline-flex items-center gap-2"><ShieldCheck size={15} className="text-[#60a5fa]" /> Contracting &amp; carrier support</span>
            <span className="inline-flex items-center gap-2"><Users size={15} className="text-[#60a5fa]" /> Training &amp; community</span>
          </motion.div>
        </motion.div>

        {/* Layered UI + photo composition */}
        <div className="lg:col-span-6 relative mb-10 sm:mb-6 lg:mb-0">
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="relative mx-auto max-w-sm sm:max-w-md lg:max-w-none"
          >
            {/* Main app-preview panel */}
            <div className="rounded-2xl border border-white/10 bg-[#0b1f45]/80 backdrop-blur-xl shadow-2xl overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-[11px] text-slate-400 font-mono">KR Agency Hub</span>
              </div>
              <div className="p-4 grid grid-cols-3 gap-3">
                {[
                  { icon: Users, label: 'Clients', val: '248' },
                  { icon: TrendingUp, label: 'Leads', val: '31' },
                  { icon: CalendarDays, label: 'This week', val: '12' },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl bg-white/5 border border-white/10 p-3">
                    <s.icon size={16} className="text-[#f5c876]" />
                    <div className="mt-2 text-xl font-bold text-white">{s.val}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="px-4 pb-4 space-y-2">
                {[
                  { name: 'Contracting · Aetna', status: 'Active', tone: 'bg-emerald-400/20 text-emerald-300' },
                  { name: 'Follow-up · Renewal call', status: 'Today', tone: 'bg-amber-400/20 text-amber-300' },
                  { name: 'New lead · Web form', status: 'New', tone: 'bg-sky-400/20 text-sky-300' },
                ].map((r) => (
                  <div key={r.name} className="flex items-center justify-between rounded-lg bg-white/[0.04] border border-white/5 px-3 py-2.5">
                    <span className="text-sm text-slate-200">{r.name}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${r.tone}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating notification chip */}
            <motion.div
              className="absolute -top-5 -left-4 sm:-left-8 rounded-xl bg-white text-slate-900 shadow-2xl px-4 py-3 flex items-center gap-3 kr-float"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              <span className="w-9 h-9 rounded-lg bg-[#0f2a5e] text-white flex items-center justify-center"><Bell size={16} /></span>
              <div>
                <div className="text-xs font-bold leading-tight">Appointment confirmed</div>
                <div className="text-[11px] text-slate-500">Carrier ready · Aetna</div>
              </div>
            </motion.div>

            {/* Floating photo + messaging chip */}
            <motion.div
              className="absolute -bottom-6 -right-3 sm:-right-8 w-44 sm:w-52 rounded-2xl overflow-hidden border-4 border-white/90 shadow-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.6 }}
            >
              <img src={CONSULT_IMG} alt="KR agent meeting with a client" className="w-full h-32 sm:h-36 object-cover" loading="eager" />
              <div className="bg-white px-3 py-2 flex items-center gap-2">
                <MessageSquare size={14} className="text-[#b8862d]" />
                <span className="text-[11px] font-semibold text-slate-700">Text &amp; call from one place</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Feature strip — the KR ecosystem at a glance */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <motion.ul
            className="grid grid-cols-3 md:grid-cols-6 gap-x-4 gap-y-6"
            variants={staggerParent}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
          >
            {[
              { icon: ShieldCheck, label: 'Carrier Opportunities' },
              { icon: Handshake, label: 'Contracting Support' },
              { icon: GraduationCap, label: 'Training & Development' },
              { icon: Megaphone, label: 'Marketing Resources' },
              { icon: Cpu, label: 'Technology & Tools' },
              { icon: LifeBuoy, label: 'Ongoing Support' },
            ].map((f) => (
              <motion.li key={f.label} variants={fadeUp} className="flex flex-col items-center text-center gap-2">
                <span className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#f5c876]">
                  <f.icon size={19} />
                </span>
                <span className="text-[11px] sm:text-xs font-medium text-slate-300 leading-tight">{f.label}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </div>

      {/* Bottom fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white/5 to-transparent pointer-events-none" />
    </section>
  );
}
